import {ChangeEvent} from 'react';
import isEqual from 'fast-deep-equal/react';
import {
  DatumValue,
  InlineData,
  InlineDataService,
  InlineDataServiceContext,
  InlineDataServiceParams,
  InlineDataServiceResponseError,
  InlineDatum,
  InlineDatumInput,
} from './types';
import {
  isInputInlineDatum,
  isReactComponentInlineDatum,
} from './helpers/datum-guard';

const RELOAD_TIMEOUT_MS = 500;

export class InlineDataState {
  private inlineData: InlineData;

  private service: InlineDataService;

  private context?: InlineDataServiceContext;

  private data: Array<InlineDatum>;

  private datumValues: Record<string, DatumValue> = {};

  private setData: (data: Array<InlineDatum>) => void;

  private reloadTimeout: number | null = null;

  actionError?: InlineDataServiceResponseError;

  fetchError?: InlineDataServiceResponseError;

  constructor(
    inlineData: InlineData,
    service: InlineDataService,
    setData: (data: Array<InlineDatum>) => void,
    context?: InlineDataServiceContext,
  ) {
    this.inlineData = inlineData;
    this.service = service;
    this.context = context;

    this.data = inlineData.data;
    this.setData = setData;

    this.initValues();
  }

  fetch(
    params?: Record<string, DatumValue>,
    updateOnChangeDatumName?: string,
  ): void {
    const fetchParams = {
      name: this.inlineData.name,
      data: this.paramsForFetch(params),
    };
    this.service.fetch(fetchParams, this.context).then((resp) => {
      this.fetchError = undefined;
      if (resp.type === 'success') {
        this.updateData(resp.data, updateOnChangeDatumName);
      } else if (resp.type === 'error') {
        this.fetchError = resp;
      }
    });
  }

  action(params: Record<string, DatumValue> = {}): void {
    const actionParams = {
      name: this.inlineData.name,
      data: params,
    };
    this.service.action(actionParams, this.context).then((resp) => {
      this.actionError = undefined;
      if (resp.type === 'success') {
        this.updateData(resp.data);
      } else if (resp.type === 'error') {
        this.actionError = resp;
      }
    });
  }

  onChange(
    datum: InlineDatumInput,
    e: ChangeEvent<HTMLInputElement> | DatumValue,
  ): void {
    const {name, update_on_change: updateOnChange} = datum;
    let value: DatumValue;
    if (e && (e as ChangeEvent<HTMLInputElement>).target) {
      value = (e as ChangeEvent<HTMLInputElement>).target.value;
    } else {
      value = e as DatumValue;
    }

    this.datumValues = {
      ...this.datumValues,
      [name]: value,
    } as const;

    this.triggerUpdatedDataWithNewValues();

    if (updateOnChange) {
      this.debouncedFetch(this.datumValues, name);
    }
  }

  onSubmit(params?: Record<string, DatumValue>): void {
    this.action(this.paramsForAction(params));
  }

  onButtonClick(params?: Record<string, DatumValue>): void {
    // in the original code, handle submit does more than handleButtonClick, but it's not clear
    // why handleButtonClick shouldn't also do those things, so for now I'm delegating to it.
    this.onSubmit(params);
  }

  clearPendingReload() {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }
  }

  private initValues() {
    const changed = this.setDefaultValues();

    if (changed) {
      this.triggerUpdatedDataWithNewValues();
    }
  }

  private setDefaultValues(): boolean {
    let changed = false;
    this.data.forEach((datum) => {
      if (
        (isReactComponentInlineDatum(datum) && !!datum.value) ||
        (isInputInlineDatum(datum) && (!!datum.value || !!datum.default_value))
      ) {
        changed = true;

        if (isReactComponentInlineDatum(datum)) {
          this.datumValues[datum.name] = datum.value;
        } else {
          this.datumValues[datum.name] = datum.value
            ? datum.value
            : datum.default_value;
        }
      }
    });

    return changed;
  }

  private debouncedFetch(
    params: Record<string, DatumValue>,
    updateOnChangeDatumName?: string,
  ): void {
    this.clearPendingReload();
    this.reloadTimeout = setTimeout(() => {
      this.fetch(params, updateOnChangeDatumName);
    }, RELOAD_TIMEOUT_MS);
  }

  private updateData(
    newData: Array<InlineDatum>,
    updateOnChangeDatumName?: string,
  ) {
    // only input and custom react components have their values updated from the server
    const newDatumValues = newData.reduce(
      (values, datum) => {
        if (isInputInlineDatum(datum)) {
          if (!values[datum.name] && datum.default_value) {
            // if the value is not set and there's a default value, set it to the default
            values[datum.name] = datum.default_value;
          } else if (
            // if the value has been updated from the server, and it's not
            // the datum that triggered the update, update the value
            values[datum.name] !== datum.value &&
            datum.name !== updateOnChangeDatumName
          ) {
            values[datum.name] = datum.value || datum.default_value;
          }
          return values;
        }

        if (isReactComponentInlineDatum(datum)) {
          const datumValue = datum.value || {};
          Object.keys(datumValue).forEach((key) => {
            if (!isEqual(values[key], datumValue[key])) {
              values[key] = datumValue[key] as DatumValue;
            }
          });
          return values;
        }

        return values;
      },
      {...this.datumValues},
    );

    this.datumValues = newDatumValues;

    this.triggerUpdatedDataWithNewValues();

    this.queueNextReload(newData);
  }

  private paramsForAction(
    params?: Record<string, DatumValue> | null,
  ): Record<string, DatumValue> {
    const hiddenInputs: Record<string, unknown> = {};
    this.data.filter(isInputInlineDatum).forEach((datum) => {
      if (datum.type === 'hidden') {
        hiddenInputs[datum.name] = datum.value;
      }
    });
    return {
      ...hiddenInputs,
      ...(params || this.datumValues),
    } as InlineDataServiceParams;
  }

  private paramsForFetch(
    params?: Record<string, DatumValue> | null,
  ): Record<string, DatumValue> {
    const filtered = this.paramsForAction(params);
    this.data.filter(isInputInlineDatum).forEach((datum) => {
      if (!datum.update_on_change && filtered[datum.name]) {
        delete filtered[datum.name];
      }
    });
    return filtered;
  }

  private triggerUpdatedDataWithNewValues() {
    const dataWithUpdatedValues = this.data.map((datum) => {
      if (isInputInlineDatum(datum)) {
        const value =
          this.datumValues[datum.name] || datum.value || datum.default_value;
        return {...datum, value};
      } else if (isReactComponentInlineDatum(datum)) {
        const value = datum.value || {};
        return {...datum, value: {...value, ...this.datumValues}};
      }
      return {...datum};
    });
    this.setData(dataWithUpdatedValues);
  }

  private queueNextReload(data: Array<InlineDatum> = this.data) {
    const nextReloadData = data.filter((datum) => !!datum.next_reload_after);
    if (nextReloadData.length > 0) {
      this.clearPendingReload();
      const minValue = Math.min(
        ...nextReloadData.map((datum) => datum.next_reload_after!),
      );
      const nextReloadAfterSeconds = Math.max(0, minValue * 1000);
      this.reloadTimeout = setTimeout(
        () => this.fetch({}),
        nextReloadAfterSeconds,
      );
    }
  }
}
